export const DASHBOARD_ENV_MODES = {
    DEV_FIRST: 'dev-first',
    LOCAL_DEBUG: 'local-debug',
    PRODUCTION: 'production'
};

const PRODUCTION_DASHBOARD_HOSTS = new Set(['dashboard.gophr.app']);
const DEVELOPMENT_DASHBOARD_HOSTS = new Set(['dev-dashboard.gophr.app']);

const getBrowserHostname = () =>
    typeof window !== 'undefined' && window.location && typeof window.location.hostname === 'string'
        ? window.location.hostname
        : '';

export const isLocalLikeHost = host =>
    typeof host === 'string' && (host.includes('localhost') || host.includes('127.0.0.1'));

export const getDashboardStage = () => {
    const browserHostname = getBrowserHostname();

    if (DEVELOPMENT_DASHBOARD_HOSTS.has(browserHostname)) {
        return 'development';
    }

    if (PRODUCTION_DASHBOARD_HOSTS.has(browserHostname)) {
        return 'production';
    }

    return process.env.REACT_APP_STAGE === 'production' ? 'production' : 'development';
};

export const isProductionStage = () => getDashboardStage() === 'production';

export const getDashboardClientEnv = () => (isProductionStage() ? 'production' : 'development');

export const resolveDashboardClientEnv = () => {
    const browserHostname = getBrowserHostname();

    if (DEVELOPMENT_DASHBOARD_HOSTS.has(browserHostname)) {
        return 'development';
    }

    if (PRODUCTION_DASHBOARD_HOSTS.has(browserHostname)) {
        return 'production';
    }

    if (process.env.REACT_APP_CLIENT_ENV === 'production') {
        return 'production';
    }

    return 'development';
};

export const getDashboardEnvironmentMode = () => {
    if (isProductionStage()) {
        return DASHBOARD_ENV_MODES.PRODUCTION;
    }

    return process.env.REACT_APP_ENV_MODE === DASHBOARD_ENV_MODES.LOCAL_DEBUG
        ? DASHBOARD_ENV_MODES.LOCAL_DEBUG
        : DASHBOARD_ENV_MODES.DEV_FIRST;
};

export const isDashboardLocalDebugMode = () => getDashboardEnvironmentMode() === DASHBOARD_ENV_MODES.LOCAL_DEBUG;

export const getHostedDashboardServiceHost = ({ developmentHost, productionHost }) =>
    isProductionStage() ? productionHost : developmentHost;

export const resolveDashboardServiceHost = ({ configuredHost, developmentHost, productionHost }) => {
    const fallbackHost = getHostedDashboardServiceHost({ developmentHost, productionHost });

    if (!configuredHost) {
        return fallbackHost;
    }

    if (isProductionStage()) {
        return configuredHost;
    }

    if (isDashboardLocalDebugMode()) {
        return configuredHost;
    }

    return isLocalLikeHost(configuredHost) ? developmentHost : configuredHost;
};

export const getRealtimeTransportPolicy = host => ({
    shouldUsePollingOnly: false,
    reconnectionAttempts: isLocalLikeHost(host) ? 1 : Infinity
});

const getDashboardHasuraTargets = resolvedConfig => ({
    shipmentsHasuraHost: resolvedConfig && resolvedConfig.api_shipments && resolvedConfig.api_shipments.admin && resolvedConfig.api_shipments.admin.hasuraHost,
    shipmentsWssHasuraHost: resolvedConfig && resolvedConfig.api_shipments && resolvedConfig.api_shipments.admin && resolvedConfig.api_shipments.admin.wssHasuraHost,
    usersHasuraHost: resolvedConfig && resolvedConfig.api_users && resolvedConfig.api_users.admin && resolvedConfig.api_users.admin.hasuraHost,
    usersHasuraRest: resolvedConfig && resolvedConfig.api_users && resolvedConfig.api_users.admin && resolvedConfig.api_users.admin.hasuraRest,
    usersWssHasuraHost: resolvedConfig && resolvedConfig.api_users && resolvedConfig.api_users.admin && resolvedConfig.api_users.admin.wssHasuraHost
});

const getMixedLocalDebugTargets = resolvedConfig => {
    if (!resolvedConfig || !resolvedConfig.environment || resolvedConfig.environment.mode !== DASHBOARD_ENV_MODES.LOCAL_DEBUG) {
        return [];
    }

    return Object.entries(getDashboardHasuraTargets(resolvedConfig))
        .filter(([, host]) => typeof host === 'string' && host.length > 0 && !isLocalLikeHost(host))
        .map(([name, host]) => ({ name, host }));
};

let hasLoggedDashboardEnvironmentSummary = false;
let hasWarnedMixedLocalDebugTargets = false;

const shouldLogDashboardEnvironmentSummary = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return false;
    }

    return window.localStorage.getItem('dashboard:debug-env') === 'true';
};

export const logDashboardEnvironmentSummary = resolvedConfig => {
    if (!resolvedConfig || !shouldLogDashboardEnvironmentSummary() || typeof console === 'undefined' || typeof console.info !== 'function') {
        return;
    }

    const mixedLocalDebugTargets = getMixedLocalDebugTargets(resolvedConfig);

    if (!hasLoggedDashboardEnvironmentSummary) {
        console.info('[dashboard-env]', {
            mode: resolvedConfig.environment && resolvedConfig.environment.mode,
            stage: resolvedConfig.environment && resolvedConfig.environment.stage,
            shipmentsHost: resolvedConfig.api_shipments && resolvedConfig.api_shipments.admin && resolvedConfig.api_shipments.admin.shipmentHost,
            shipmentsHasuraHost: resolvedConfig.api_shipments && resolvedConfig.api_shipments.admin && resolvedConfig.api_shipments.admin.hasuraHost,
            shipmentsWssHasuraHost: resolvedConfig.api_shipments && resolvedConfig.api_shipments.admin && resolvedConfig.api_shipments.admin.wssHasuraHost,
            shipmentsMerchantHost: resolvedConfig.api_shipments && resolvedConfig.api_shipments.merchant && resolvedConfig.api_shipments.merchant.shipmentHost,
            usersHost: resolvedConfig.api_users && resolvedConfig.api_users.admin && resolvedConfig.api_users.admin.usersHost,
            usersHasuraHost: resolvedConfig.api_users && resolvedConfig.api_users.admin && resolvedConfig.api_users.admin.hasuraHost,
            usersHasuraRest: resolvedConfig.api_users && resolvedConfig.api_users.admin && resolvedConfig.api_users.admin.hasuraRest,
            usersWssHasuraHost: resolvedConfig.api_users && resolvedConfig.api_users.admin && resolvedConfig.api_users.admin.wssHasuraHost,
            paymentsHost: resolvedConfig.api_payments && resolvedConfig.api_payments.host,
            filesHost: resolvedConfig.api_files && resolvedConfig.api_files.host,
            inventoryHost: resolvedConfig.api_inventory && resolvedConfig.api_inventory.admin && resolvedConfig.api_inventory.admin.inventoryHost,
            inventoryHasuraHost: resolvedConfig.api_inventory && resolvedConfig.api_inventory.admin && resolvedConfig.api_inventory.admin.hasuraHost,
            mixedLocalDebugTargets
        });
        hasLoggedDashboardEnvironmentSummary = true;
    }

    if (mixedLocalDebugTargets.length === 0 || typeof console.warn !== 'function' || hasWarnedMixedLocalDebugTargets) {
        return;
    }

    const warningMessage = '[dashboard-env] local-debug is using hosted GraphQL/Hasura targets, so the dashboard can still show hosted-dev orders even when REST points to localhost.';

    console.warn(warningMessage, mixedLocalDebugTargets);
    hasWarnedMixedLocalDebugTargets = true;

    if (process.env.REACT_APP_REQUIRE_LOCAL_HASURA === 'true') {
        throw new Error(`${warningMessage} Set REACT_APP_API_*_HASURA_HOST overrides to local targets or use the mixed local launch intentionally.`);
    }
};